/**
 * Watches for a newly deployed frontend build (power#1224).
 *
 * Polls `/version.json` every minute and raises `pendingVersion` when the
 * deployed build differs from the one this tab is running. `NewVersionBanner`
 * renders off that flag.
 *
 * This hook deliberately does NOTHING else: it never reloads, never releases a
 * lock, never navigates. Acting on the flag is the user's decision, taken via
 * the banner.
 *
 * The comparison is "different from mine", not "newer than mine": a rollback
 * re-deploys an OLDER commit sha, and that is still a bundle this tab must pick
 * up. `builtAt` is never compared.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { logger } from "@/services/logger";
import { useAppVersionStore } from "@/stores/useAppVersionStore";
import {
  RUNNING_VERSION,
  checkPostReloadSanity,
  fetchDeployedVersion,
  hasAlreadyReloadedFor,
  isVersionWatchEnabled,
  isWithinReloadCooldown,
} from "@/utils/appVersion";

// One minute. The poll is a single conditional GET of a ~100-byte file that
// hidden tabs skip entirely (`refetchIntervalInBackground: false`), so the cost
// of the shorter interval is negligible and the win is that a deploy reaches
// open tabs within a minute instead of up to three.
//
// This equals `RELOAD_COOLDOWN_MS` in `utils/appVersion.ts`, which is fine.
// After a reload at R the tab re-mounts and fetches at R+boot (staleTime 0, no
// cache), then every POLL_MS; the second poll therefore lands outside the
// [R, R+60s) cooldown for any interval >= ~58s. The first poll is inside it at
// ANY interval, including the old 3 minutes.
//
// It rarely matters either way: the effect below returns at
// `data.version === RUNNING_VERSION` (reload worked) or at
// `hasAlreadyReloadedFor` (it didn't) long before the cooldown is consulted.
// Reaching the cooldown needs a THIRD version to appear within 60s of a
// reload — a second deploy inside a one-minute window — and costs that banner
// one cycle.
const POLL_MS = 60 * 1000;

export function useAppVersionWatcher(): void {
  const enabled = isVersionWatchEnabled();
  const setPending = useAppVersionStore((state) => state.setPending);
  const clearPending = useAppVersionStore((state) => state.clearPending);

  /**
   * Builds we must stop offering, because we already reloaded for one and came
   * back running something else. Without this the banner would reappear on
   * every poll and the user could click forever.
   *
   * A Set rather than a single value: `sessionStorage` remembers only the LAST
   * build we reloaded for, so an edge serving two builds inconsistently would
   * otherwise alternate — B ignored, C offered, reload, C ignored, B offered
   * again — and nag indefinitely. Accumulating them in the document bounds that.
   */
  const ignoredVersionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!enabled) return;
    const stale = checkPostReloadSanity();
    if (stale) ignoredVersionsRef.current.add(stale);
  }, [enabled]);

  const { data } = useQuery({
    queryKey: ["app-version"],
    enabled,
    queryFn: fetchDeployedVersion,
    refetchInterval: POLL_MS,
    // Hidden tabs stop polling; refetchOnWindowFocus then catches the
    // long-idle tab the moment the user comes back to it.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
    retry: false, // a failed poll is a no-op, not an error to surface
  });

  useEffect(() => {
    if (!enabled || !data) return;

    if (data.version === RUNNING_VERSION) {
      clearPending();
      return;
    }
    if (ignoredVersionsRef.current.has(data.version)) return;
    if (hasAlreadyReloadedFor(data.version)) {
      // We already reloaded for this build and are still not running it.
      ignoredVersionsRef.current.add(data.version);
      return;
    }
    // A deploy is not atomic across CloudFront's async invalidation, so just
    // after a reload the edge can briefly serve an index.html and a
    // version.json that disagree. Hold off rather than immediately re-offering;
    // the poll keeps running, so a real new build still surfaces shortly.
    if (isWithinReloadCooldown()) return;

    logger.info("New frontend build detected", {
      running: RUNNING_VERSION,
      deployed: data.version,
      deployedAt: data.builtAt ?? undefined,
    });
    setPending(data.version, data.builtAt);
  }, [enabled, data, setPending, clearPending]);
}
